import React, { useEffect, useState } from 'react';
import './App.css';
import { DecisionProvider } from './context/DecisionContext';
import SettingsPanel from './components/SettingsPanel';
import QuestionForm from './components/QuestionForm';
import DecisionTreeView from './components/DecisionTreeView';
import DecisionFlowView from './components/DecisionFlowView';
import DecisionCompareView from './components/DecisionCompareView';
import DecisionRecordsView from './components/DecisionRecordsView';
import ViewDecisionPage from './components/ViewDecisionPage';
import CloudSyncPanel from './components/CloudSyncPanel';
import cloudSyncService from './services/cloudSync';
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';

function HomePage() {
  const [viewMode, setViewMode] = useState<'tree' | 'flow'>(() => {
    // 从localStorage加载用户偏好
    const savedMode = localStorage.getItem('viewMode');
    return (savedMode as 'tree' | 'flow') || 'tree';
  });

  // 保存视图模式到localStorage
  useEffect(() => {
    localStorage.setItem('viewMode', viewMode);
  }, [viewMode]);

  return (
    <>
      <div className="w-full max-w-3xl">
        <SettingsPanel />
        <CloudSyncPanel />
      </div>
      <QuestionForm />
      
      <div className="w-full max-w-6xl">
        <div className="bg-white p-4 rounded-lg shadow-sm border">
          <div className="flex justify-between items-center mb-2">
            <Link 
              to="/compare" 
              className="text-sm text-indigo-600 hover:text-indigo-800 transition-colors flex items-center"
            >
              <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
              </svg>
              比较决策树
            </Link>
            
            <div className="flex rounded-md shadow-sm" role="group">
              <button
                className={`px-4 py-2 text-sm font-medium ${
                  viewMode === 'tree'
                    ? 'bg-indigo-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                } border border-gray-300 rounded-l-md transition-colors`}
                onClick={() => setViewMode('tree')}
              >
                列表视图
              </button>
              <button
                className={`px-4 py-2 text-sm font-medium ${
                  viewMode === 'flow'
                    ? 'bg-indigo-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                } border border-gray-300 rounded-r-md transition-colors`}
                onClick={() => setViewMode('flow')}
              >
                节点视图
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {viewMode === 'tree' ? <DecisionTreeView /> : <DecisionFlowView />}
    </>
  );
}

function App() {
  const [lastSyncTime, setLastSyncTime] = useState<Date | null>(null);

  // 设置自动同步
  useEffect(() => {
    const syncConfig = cloudSyncService.getSyncConfig();
    
    // 如果启用了自动同步
    if (syncConfig.enabled && syncConfig.autoSync) {
      // 设置同步定时器
      const syncInterval = Math.max(5, syncConfig.syncInterval || 60) * 60 * 1000; // 转换为毫秒
      
      const performSync = async () => {
        try {
          if (cloudSyncService.isSyncEnabled()) {
            console.log('执行自动同步...');
            const result = await cloudSyncService.syncRecords();
            setLastSyncTime(new Date());
            console.log('自动同步结果:', result);
          }
        } catch (error) {
          console.error('自动同步失败:', error);
        }
      };
      
      // 启动同步定时器
      const syncTimer = setInterval(performSync, syncInterval);
      
      // 初始化时执行一次同步
      performSync();
      
      // 清理定时器
      return () => clearInterval(syncTimer);
    }
  }, []);

  return (
    <DecisionProvider>
      <Router>
        <div className="min-h-screen bg-gray-50">
          <header className="bg-indigo-700 shadow-lg">
            <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8 flex justify-between items-center">
              <div>
                <Link to="/" className="text-white hover:text-indigo-100">
                  <h1 className="text-2xl font-bold text-white">AI决策树系统</h1>
                </Link>
                <p className="mt-1 text-indigo-200">输入问题，获取详细的决策分析和概率评估</p>
              </div>
              <div className="flex items-center space-x-4">
                {lastSyncTime && (
                  <span className="text-xs text-indigo-200">
                    上次同步: {lastSyncTime.toLocaleTimeString()}
                  </span>
                )}
                <div className="flex space-x-2">
                  <Link
                    to="/compare"
                    className="px-4 py-2 border border-indigo-300 text-indigo-100 rounded-md hover:bg-indigo-600 transition-colors"
                  >
                    比较决策
                  </Link>
                  <Link
                    to="/records"
                    className="px-4 py-2 border border-indigo-300 text-indigo-100 rounded-md hover:bg-indigo-600 transition-colors"
                  >
                    决策记录
                  </Link>
                </div>
              </div>
            </div>
          </header>

          <main className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col items-center">
              <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/records" element={<DecisionRecordsView />} />
                <Route path="/view-decision" element={<ViewDecisionPage />} />
                <Route path="/compare" element={<DecisionCompareView />} />
              </Routes>
            </div>
          </main>

          <footer className="bg-white mt-12 py-8 border-t">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <p className="text-center text-gray-500 text-sm">
                AI决策树系统 © {new Date().getFullYear()} - 使用AI生成结构化的决策分析
              </p>
            </div>
          </footer>
        </div>
      </Router>
    </DecisionProvider>
  );
}

export default App;
